import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MaskedLine } from "@/components/Reveal";

const OPENING_IMAGES = [
    { src: "/assets/img/hero.jpg", alt: "A business running on a connected HIGHTS digital ecosystem" },
    { src: "/assets/img/ecosystem.jpg", alt: "Website, CRM, bookings and analytics connected as one system" },
    { src: "/assets/img/ai-employee.jpg", alt: "A HIGHTS AI employee handling enquiries automatically" },
    { src: "/assets/img/devices.jpg", alt: "A bespoke HIGHTS website across laptop, tablet and phone" },
];

export default function Opening({ onEnter }) {
    const [img, setImg] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setImg((i) => (i + 1) % OPENING_IMAGES.length), 3200);
        return () => clearInterval(id);
    }, []);

    return (
        <motion.div
            className="fixed inset-0 z-[95] flex flex-col items-center justify-center overflow-hidden bg-black px-6"
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
            data-testid="opening-sequence"
        >
            <div className="absolute inset-0" aria-hidden="true">
                <AnimatePresence mode="sync">
                    <motion.img
                        key={img}
                        src={OPENING_IMAGES[img].src}
                        alt=""
                        initial={{ opacity: 0, scale: 1.06 }}
                        animate={{ opacity: 0.32, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.8, ease: "easeOut" }}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-black/85" />
            </div>

            <motion.p
                initial={{ opacity: 0, letterSpacing: "1em" }}
                animate={{ opacity: 1, letterSpacing: "0.55em" }}
                transition={{ duration: 1.6, delay: 0.2 }}
                className="relative z-10 font-display text-sm font-bold text-white/60"
            >
                HIGHTS
            </motion.p>
            <div className="relative z-10 mt-12 text-center">
                <MaskedLine delay={0.9}>
                    <span className="font-display text-4xl font-black leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-7xl">
                        WHAT IF YOUR BUSINESS
                    </span>
                </MaskedLine>
                <MaskedLine delay={1.25}>
                    <span className="font-display text-4xl font-black leading-[1.02] tracking-tighter text-white sm:text-6xl lg:text-7xl">
                        NEVER STOPPED WORKING?
                    </span>
                </MaskedLine>
            </div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.3, duration: 1 }}
                className="relative z-10 mt-12 space-y-2 text-center text-sm font-light tracking-wide text-white/45 md:text-base"
            >
                <p>Not another website.</p>
                <p>Not another marketing package.</p>
                <p className="pt-2 text-white">A connected growth system.</p>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 3.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mt-14 flex w-full max-w-md flex-col gap-3 sm:max-w-none sm:flex-row sm:gap-4"
            >
                <button
                    onClick={onEnter}
                    data-testid="experience-hights-button"
                    className="bg-white px-10 py-4 text-xs font-bold uppercase tracking-[0.28em] text-black transition-colors duration-300 hover:bg-white/80"
                >
                    Experience HIGHTS
                </button>
                <Link
                    to="/book"
                    data-testid="opening-book-button"
                    className="border border-white/25 bg-black/40 px-10 py-4 text-center text-xs font-bold uppercase tracking-[0.28em] text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/10"
                >
                    Book a 10–20 min consultation
                </Link>
            </motion.div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 3.8 }}
                className="absolute bottom-8 z-10 flex items-center gap-3"
                aria-hidden="true"
            >
                {OPENING_IMAGES.map((im, i) => (
                    <span key={im.src} className={`h-1 transition-all duration-500 ${i === img ? "w-8 bg-white" : "w-3 bg-white/25"}`} />
                ))}
                <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.3em] text-white/25">
                    A cinematic demonstration of what HIGHTS builds
                </span>
            </motion.div>
        </motion.div>
    );
}
